import Button from '@ingka/button';
import Skeleton from '@ingka/skeleton';
import SSRIcon from '@ingka/ssr-icon';
import arrowDownIcon from '@ingka/ssr-icon/paths/arrow-down-to-line';
import documentIcon from '@ingka/ssr-icon/paths/document';
import ellipsesHorizontalIcon from '@ingka/ssr-icon/paths/ellipses-horizontal';
import imageIcon from '@ingka/ssr-icon/paths/image';
import type { TranslateFunction } from '@repo/phrase';
import type { AssetAttachment, DownloadAttachmentFunction, FormatDateTimeFunction } from '@repo/types/attachment';
import type { LogErrorFunction } from '@repo/utils';

import { Heading, Paragraph } from '../typography/typography.component';
import { useAttachment } from './attachment.hooks';
import { ATTACHMENT_IMAGE_FILE_TYPES } from './constants';

import styles from './attachment-item.module.scss';

interface AttachmentItemProps {
    item: AssetAttachment;
    translate: TranslateFunction;
    formatDateTime: FormatDateTimeFunction;
    downloadAttachment: DownloadAttachmentFunction;
    logError: LogErrorFunction;
    downloadButtonLabel: string;
    onEditClick?: (() => void) | undefined;
}

export const AttachmentItem = ({
    item,
    translate,
    formatDateTime,
    downloadAttachment,
    logError,
    downloadButtonLabel,
    onEditClick,
}: AttachmentItemProps) => {
    const { downloadFile } = useAttachment(item, downloadAttachment, logError);
    const fileType = item.fileType;
    const fileName = item.originalFileName;
    const fileDate = item.uploadedDate;
    const comment = item.attachmentComment;
    const isImage = fileType !== undefined && ATTACHMENT_IMAGE_FILE_TYPES.includes(fileType);

    return (
        <>
            <div>
                <SSRIcon paths={isImage ? imageIcon : documentIcon} className={styles.file_icon ?? ''} />
                <div className={styles.file_info}>
                    <Heading headingSize="xs">{fileName}</Heading>
                    {comment && (
                        <Paragraph bodySize="s" className={styles.file_comment ?? ''}>
                            {comment}
                        </Paragraph>
                    )}
                </div>
            </div>
            <div>{fileDate ? <Paragraph bodySize="s">{formatDateTime(fileDate)}</Paragraph> : <Skeleton />}</div>
            <div>
                <Button
                    iconOnly
                    size="small"
                    type="tertiary"
                    ssrIcon={arrowDownIcon}
                    onClick={() => {
                        void downloadFile();
                    }}
                    data-testid="download-attachment"
                    aria-label={translate(downloadButtonLabel)}
                />
            </div>
            {onEditClick && (
                <div>
                    <Button
                        iconOnly
                        size="small"
                        type="tertiary"
                        ssrIcon={ellipsesHorizontalIcon}
                        onClick={onEditClick}
                        data-testid="edit-attachment"
                        aria-label={translate('attachments_edit_button_label')}
                    />
                </div>
            )}
        </>
    );
};
